import chroma from 'chroma-js';

export const getLightenedColors = (color: string) => {
  const chromaColor = chroma(color);
  return {
    color100: chromaColor,
    color90: chromaColor.mix('white', 0.1),
    color75: chromaColor.mix('white', 0.25),
    color50: chromaColor.mix('white', 0.5),
    color25: chromaColor.mix('white', 0.75),
    color10: chromaColor.mix('white', 0.9),
  };
};

// $blue1 and $blue0. The signed-in portal falls back to these too, so they live here rather than
// being repeated per call site.
export const DEFAULT_PORTAL_PRIMARY_COLOR = '#0070ae';
export const DEFAULT_PORTAL_SECONDARY_COLOR = '#f2f8fb';
export const MIN_PRIMARY_ON_WHITE_CONTRAST = 4.5;

// The primary button on the pre-auth pages is white text on --primary-color, so a pale or
// invalid lender colour would leave the only button on the page unreadable or throw in chroma.
// The answer is always normalised to opaque 6-digit hex, because callers feed it to helpers that
// only parse #rgb and #rrggbb. Without alpha(1) a colour carrying alpha would come back as
// 8-digit hex, which those helpers mis-parse rather than reject.
export const getAccessiblePrimaryColor = (color: string | undefined): string => {
  if (!color || !chroma.valid(color)) return DEFAULT_PORTAL_PRIMARY_COLOR;
  return chroma.contrast(color, 'white') >= MIN_PRIMARY_ON_WHITE_CONTRAST
    ? chroma(color).alpha(1).hex()
    : DEFAULT_PORTAL_PRIMARY_COLOR;
};

interface PortalCssOptions {
  primaryColor: string;
  secondaryColor: string;
  logoHeightMobile?: string;
  logoHeightDesktop?: string;
}

export const getPortalCss = (options: PortalCssOptions) => {
  const { primaryColor, secondaryColor, logoHeightMobile, logoHeightDesktop } = options;
  const primaryColors = getLightenedColors(primaryColor);
  return (
    <style type="text/css">
      {`
        :root {
          --primary-color: ${primaryColors.color100};
          --primary-color-90: ${primaryColors.color90};
          --primary-color-75: ${primaryColors.color75};
          --primary-color-50: ${primaryColors.color50};
          --primary-color-25: ${primaryColors.color25};
          --primary-color-10: ${primaryColors.color10};
          --secondary-color: ${secondaryColor};
          ${logoHeightMobile ? `--logo-height-mobile: ${logoHeightMobile};` : ''}
          ${logoHeightDesktop ? `--logo-height-desktop: ${logoHeightDesktop};` : ''}
        }
      `}
    </style>
  );
};
